import {
    PropsWithChildren,
    createContext,
    useContext,
    useMemo,
    useState
} from "react";

import type { LoadAdditionalItems } from "../components/filterable-gallery/src/types";
import type { SearchConfiguration } from "../components/filterable-gallery/src/utils/filterEngine";

export interface FilterableGalleryLoaderRegistration {
    loader: LoadAdditionalItems;
}

/**
 * The gallery's items + search config + featured slugs, published for the
 * sibling search bar so both run the same search pipeline over the same data.
 */
export interface FilterableGalleryConfig {
    items: unknown[];
    searchConfiguration?: SearchConfiguration;
    featuredItemSlugs: string[];
}

interface FilterableGalleryLoaderContextValue {
    registerLoader: (
        registration: FilterableGalleryLoaderRegistration | null
    ) => void;
    currentRegistration: FilterableGalleryLoaderRegistration | null;
    extraItems: unknown[];
    setExtraItems: (items: unknown[]) => void;
    galleryConfig: FilterableGalleryConfig | null;
    registerGalleryConfig: (config: FilterableGalleryConfig) => void;
}

const FilterableGalleryLoaderContext =
    createContext<FilterableGalleryLoaderContextValue | null>(null);

interface FilterableGalleryLoaderProviderProps {
    onLoaderRegistered?: (
        registration: FilterableGalleryLoaderRegistration | null
    ) => void;
    currentRegistration?: FilterableGalleryLoaderRegistration | null;
    extraItems?: unknown[];
    setExtraItems?: (items: unknown[]) => void;
}

export const FilterableGalleryLoaderProvider = ({
    children,
    onLoaderRegistered = () => undefined,
    currentRegistration = null,
    extraItems = [],
    setExtraItems = () => undefined
}: PropsWithChildren<FilterableGalleryLoaderProviderProps>): JSX.Element => {
    // Owned internally so neither the container nor the standalone gallery has
    // to thread it through props; the gallery publishes, the search bar reads.
    const [galleryConfig, setGalleryConfig] =
        useState<FilterableGalleryConfig | null>(null);

    const value = useMemo<FilterableGalleryLoaderContextValue>(
        () => ({
            registerLoader: onLoaderRegistered,
            currentRegistration,
            extraItems,
            setExtraItems,
            galleryConfig,
            registerGalleryConfig: setGalleryConfig
        }),
        [
            onLoaderRegistered,
            currentRegistration,
            extraItems,
            setExtraItems,
            galleryConfig
        ]
    );
    return (
        <FilterableGalleryLoaderContext.Provider value={value}>
            {children}
        </FilterableGalleryLoaderContext.Provider>
    );
};

export const useFilterableGalleryLoader =
    (): FilterableGalleryLoaderContextValue | null =>
        useContext(FilterableGalleryLoaderContext);

export const useIsInsideLoaderProvider = (): boolean =>
    useContext(FilterableGalleryLoaderContext) !== null;
